import { Form } from 'react-bootstrap';

const DEFAULT_SEPARATOR = ';';
const DEFAULT_HINT = 'Hold Ctrl (Windows) or Cmd (Mac) to select multiple.';

/**
 * Reusable multi-select dropdown. Value is stored as a delimiter-separated string.
 *
 * @param {string} name - Field name (for accessibility)
 * @param {string} label - Label text
 * @param {string} value - Stored value (delimiter-separated)
 * @param {Array<{ value: string, label: string }>} options - Option list
 * @param {string} [separator=';'] - Delimiter for value string
 * @param {(value: string | null) => void} onChange - Called with new delimiter-separated string or null
 * @param {boolean} [disabled] - Disable the select
 * @param {string} [error] - Error message to show
 * @param {number} [size=5] - Number of visible rows in the select
 * @param {(raw: string) => string} [displayTransform] - Optional transform when parsing value (e.g. legacy codes to labels)
 * @param {string} [hintText] - Hint below the select (default: Ctrl/Cmd to select multiple)
 */
const MultiSelectDropdown = ({
  name,
  label,
  value,
  options,
  separator = DEFAULT_SEPARATOR,
  onChange,
  disabled = false,
  error = '',
  size = 5,
  displayTransform = (v) => (v || '').trim(),
  hintText = DEFAULT_HINT,
}) => {
  const rawParts = (value || '')
    .split(separator)
    .map((s) => (s || '').trim())
    .filter(Boolean);
  const selectedList = rawParts.map(displayTransform).filter(Boolean);

  const optionsList = [...options];
  rawParts.forEach((raw) => {
    if (raw && !optionsList.some((o) => o.value === raw)) {
      optionsList.push({ value: raw, label: displayTransform(raw) || raw });
    }
  });

  const handleChange = (e) => {
    const selected = Array.from(e.target.selectedOptions, (opt) => opt.value);
    const nextValue = selected.length ? selected.join(separator) : null;
    onChange(nextValue);
  };

  return (
    <Form.Group>
      <Form.Label>{label}</Form.Label>
      <select
        name={name}
        className={`form-control ${error ? 'is-invalid' : ''}`}
        multiple
        value={rawParts}
        disabled={disabled}
        onChange={handleChange}
        size={size}
      >
        {optionsList.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      <div className="text-muted mt-2" >
        Selected: <strong style={{ fontSize: '1rem' }}>{selectedList.length > 0 ? selectedList.join(', ') : 'none'}</strong>
      </div>
      {hintText && <Form.Text muted style={{ fontSize: '0.85rem' }}>{hintText}</Form.Text>}
      {error && <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>}
    </Form.Group>
  );
};

export default MultiSelectDropdown;
